/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import type { ReactElement, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useSite } from '@salesforce/storefront-next-runtime/site-context';

import type { ShopperBasketsV2 } from '@/scapi';
import { Link } from '@/components/link';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { UITarget } from '@/targets/ui-target';
import { formatCurrency } from '@/lib/currency';
import { getOrCreateCheckoutCorrelationId } from '@/lib/checkout/correlation';
import type { CartInventoryValidationResult } from '@/lib/cart/inventory-validation';
import { routes } from '@/route-paths';
import { CartInventoryErrorBanner } from './cart-inventory-error-banner';
import CartPromoCode from './cart-promo-code';

const INVENTORY_ERROR_ID = 'cart-inventory-error';

interface CartOrderSummaryProps {
    basket: ShopperBasketsV2.schemas['Basket'];
    inventoryValidation?: CartInventoryValidationResult;
}

function SummaryRow({ label, children }: { label: ReactNode; children: ReactNode }): ReactElement {
    return (
        <div className="flex w-full items-start gap-6">
            <dt className="min-w-0 flex-1 break-words">{label}:</dt>
            <dd className="min-w-0 flex-1 text-right break-words">{children}</dd>
        </div>
    );
}

/**
 * Price Shoes cart order summary: subtotal, one row per cart-level promotion (named after the
 * promotion), shipping, the coupon section, total and the "Finalizar compra" / "Seguir comprando" CTAs.
 *
 * Product-level promotions are already reflected in the subtotal and shown on each line item, and
 * coupon discounts are shown in the coupon row, so neither is listed as a promotion row here.
 */
export default function CartOrderSummary({ basket, inventoryValidation }: CartOrderSummaryProps): ReactElement {
    const { t, i18n } = useTranslation('cart');
    const { currency: siteCurrency } = useSite();
    const currency = basket.currency ?? siteCurrency;
    const format = (value: number) => formatCurrency(value, i18n.language, currency);

    const cartPromotions = (basket.orderPriceAdjustments ?? []).filter((adjustment) => !adjustment.couponCode);
    const hasInventoryIssues = inventoryValidation?.hasInventoryIssues ?? false;

    const renderShipping = () => {
        if (basket.shippingTotal === 0) {
            return t('summary.free');
        }
        if (typeof basket.shippingTotal === 'number' && basket.shippingTotal > 0) {
            return format(basket.shippingTotal);
        }
        return t('summary.shippingTbd');
    };

    return (
        <Card
            className="gap-0 py-0 [--ui-radius:var(--radius-lg)] [--ui-shadow:0_2px_23px_rgb(0_0_0/0.07)]"
            data-testid="sf-cart-order-summary">
            <div className="py-6 pr-4 pl-6">
                <h2 className="text-xl font-semibold leading-[30px] text-card-foreground">
                    {t('summary.orderSummary')}
                </h2>
            </div>
            <div className="flex flex-col gap-6 px-6 pb-6">
                <section className="flex flex-col gap-6" aria-label={t('summary.orderSummary')}>
                    <div className="flex flex-col gap-3">
                        <dl className="flex flex-col gap-3 text-base font-light leading-6 text-foreground">
                            <SummaryRow label={t('summary.subtotal')}>{format(basket.productSubTotal ?? 0)}</SummaryRow>
                            {cartPromotions.map((adjustment) => (
                                <SummaryRow
                                    key={adjustment.priceAdjustmentId}
                                    label={adjustment.itemText || t('summary.discounts')}>
                                    {format(adjustment.price ?? 0)}
                                </SummaryRow>
                            ))}
                            <SummaryRow label={t('summary.shipping')}>{renderShipping()}</SummaryRow>
                            {/* Tax is already included in prices for gross taxation */}
                            {basket.taxation !== 'gross' && (
                                <SummaryRow label={t('summary.tax')}>
                                    {typeof basket.taxTotal === 'number' && basket.taxTotal >= 0
                                        ? format(basket.taxTotal)
                                        : t('summary.taxTbd')}
                                </SummaryRow>
                            )}
                        </dl>
                        <CartPromoCode basket={basket} />
                    </div>
                    <div className="flex flex-col gap-3">
                        <hr className="border-border" />
                        <dl className="text-lg font-bold leading-7 text-foreground">
                            <SummaryRow label={t('summary.total')}>
                                {format(basket.orderTotal ?? basket.productTotal ?? 0)}
                            </SummaryRow>
                        </dl>
                        <hr className="border-border" />
                    </div>
                </section>

                <div className="flex flex-col gap-3 [--ui-radius:var(--radius-lg)]">
                    {inventoryValidation && (
                        <CartInventoryErrorBanner
                            issues={inventoryValidation.itemsExceedingInventory}
                            id={INVENTORY_ERROR_ID}
                        />
                    )}
                    <Button
                        asChild={!hasInventoryIssues}
                        className="h-[46px] w-full px-[22px] text-base font-normal leading-6"
                        disabled={hasInventoryIssues}
                        aria-disabled={hasInventoryIssues}
                        aria-describedby={hasInventoryIssues ? INVENTORY_ERROR_ID : undefined}>
                        {hasInventoryIssues ? (
                            <span>{t('checkout.proceedToCheckout')}</span>
                        ) : (
                            <Link to={routes.checkout} onClick={() => getOrCreateCheckoutCorrelationId()}>
                                {t('checkout.proceedToCheckout')}
                            </Link>
                        )}
                    </Button>
                    <UITarget targetId="sfcc.cart.payments.expressCheckout" />
                    <Button
                        asChild
                        variant="ghost"
                        className="h-[46px] w-full text-base font-normal leading-6 text-primary hover:bg-transparent hover:text-primary hover:underline">
                        <Link to={routes.home}>{t('checkout.continueShopping')}</Link>
                    </Button>
                </div>
            </div>
        </Card>
    );
}
